import * as Popover from '@radix-ui/react-popover';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import TextField from 'components/TextField';

export interface HeaderDropdownProps<Item> {
	activeText: string;
	filterFn: (i: Item, searchQuery: string) => boolean;
	renderTextFn: (i: Item) => React.ReactNode | null;
	renderNewFn?: () => React.ReactNode | null;
	label: string | ReactNode;
	options: Item[];
	onSelect: (item: Item) => void;
	isWorking?: boolean;
	hideUnderline?: boolean;
	className?: string;
}

export function HeaderDropdown<Item>({
	activeText,
	label,
	options,
	filterFn,
	renderTextFn,
	renderNewFn,
	isWorking,
	hideUnderline,
	className,
	onSelect: handleSelect,
}: HeaderDropdownProps<Item>) {
	const [filteredOptions, setFilteredOptions] = useState(options);
	const [searchQuery, setSearchQuery] = useState('');
	// Measure the selected label so full-name tooltips appear only when it is cut off.
	const activeTextRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useEffect(() => {
		const element = activeTextRef.current;
		if (!element) return;

		const updateTruncation = () => setIsTruncated(element.scrollWidth > element.clientWidth);
		updateTruncation();

		// Recheck when responsive layout changes resize the label, and clean up on selection changes or unmount.
		const observer = new ResizeObserver(updateTruncation);
		observer.observe(element);
		return () => observer.disconnect();
	}, [activeText]);

	// filter the list whenever the user is typing into the search field
	useEffect(() => {
		setFilteredOptions(
			options.filter((item: Item) => {
				return filterFn(item, searchQuery);
			})
		);
	}, [filterFn, options, searchQuery]);

	// local utility to clear the search query
	const clearSearchQuery = useCallback(() => {
		setSearchQuery('');
	}, [setSearchQuery]);

	return (
		<div className={classNames('flex relative xl:mr-2', className)}>
			<Popover.Root
				onOpenChange={() => {
					clearSearchQuery();
				}}
			>
				<div className='flex items-center'>
					<span>{label}</span>
					{/* Share the dropdown button for hover and keyboard-focus tooltips without nesting buttons. */}
					<Tooltip.Root>
						<Tooltip.Trigger asChild>
							<Popover.Trigger
								className={classNames(
									'flex items-center justify-between w-[200px] ml-2 py-1',
									!hideUnderline ? 'border-b border-blue-500' : 'border-b-0',
									'text-blue-500'
								)}
							>
								{/* Let the label truncate within the compact selector while keeping the arrow visible. */}
								<span ref={activeTextRef} className='min-w-0 truncate'>
									{activeText}
								</span>
								<span className='material-symbol shrink-0'>expand_more</span>
							</Popover.Trigger>
						</Tooltip.Trigger>
						{/* Show the complete name in a viewport-constrained, wrapping tooltip only when needed. */}
						{isTruncated && (
							<Tooltip.Portal>
								<Tooltip.Content
									side='bottom'
									sideOffset={5}
									collisionPadding={12}
									className='max-w-[min(400px,calc(100vw-24px))] whitespace-normal break-words bg-blue-500 text-white px-4 py-3 rounded text-p1 z-tooltip'
								>
									{activeText}
									<Tooltip.Arrow className='fill-blue-500' />
								</Tooltip.Content>
							</Tooltip.Portal>
						)}
					</Tooltip.Root>
				</div>
				<Popover.Portal>
					{/* Give names more room while preserving a 12px margin on each side of small screens. */}
					<Popover.Content
						side='bottom'
						sideOffset={5}
						align='start'
						collisionPadding={12}
						className='w-[400px] max-w-[calc(100vw-24px)] z-topMenu bg-white rounded border border-blue-500 drop-shadow-md'
					>
						<div className='p-2'>
							<TextField
								onInput={(e: React.KeyboardEvent<HTMLInputElement>) => {
									setSearchQuery((e.target as HTMLTextAreaElement).value);
								}}
								label=''
								icon='search'
								placeholder='Search by name'
								iconClass='text-blue-600'
								sizeY='md'
								isWorking={isWorking}
							/>
						</div>
						<ul className='text-p2 w-full pb-2 overflow-y-auto max-h-72'>
							{renderNewFn && <li>{renderNewFn()}</li>}
							{filteredOptions.map((item, index) => {
								return (
									<li
										key={index}
										onClick={() => {
											handleSelect(item);
										}}
									>
										<Popover.Close
											className={classNames(
												'w-full min-w-[125px] block my-1 p-3 py-2',
												// Wrap full option names, including long words, so they remain fully readable.
												'whitespace-normal break-words text-left font-secondary font-regular text-gray-600',
												'hover:bg-blue-50'
											)}
										>
											{renderTextFn(item)}
										</Popover.Close>
									</li>
								);
							})}
						</ul>
					</Popover.Content>
				</Popover.Portal>
			</Popover.Root>
		</div>
	);
}

export default HeaderDropdown;
